@if (isLoading) {
<div class="loader-overlay" role="status" aria-live="polite">
  <div class="heart-wrapper" aria-hidden="true">
    <svg class="heart-svg" viewBox="0 0 24 24">
      <path class="heart-shape" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5
               2 6 4 4 6.5 4 8.24 4 9.91 5 10.54 6.36
               11.17 5 12.76 4 14.5 4 17 4 19 6 19 8.5
               c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      <polyline class="ekg" points="3 12 7 12 9 8 11 15 13 6 15 12 21 12" fill="none" />
    </svg>
  </div>

  <p class="loader-text mt-3 fw-semibold text-dark">Loading doctor portal...</p>
</div>
}

@if (!isLoading) {
<div class="doctor-dashboard-container">
  <app-portal-navigation portalName="Doctor Portal" portalIcon="bi-heart-pulse-fill" collapseId="doctorMenu"
    [firstName]="doctorData?.user?.firstName ?? ''" [lastName]="doctorData?.user?.lastName ?? ''"
    [primaryItems]="primaryNavigationItems" profileRoute="/dashboard/profile" [clinicMemberships]="clinicMemberships"
    [showSettings]="isLinkedToClinic" (switchClinic)="onSwitchClinic($event)">
  </app-portal-navigation>

  <div class="container-fluid p-3 p-md-4">
    <!-- Hero Banner matching booking-view -->
    <div class="booking-hero card border-0 mb-4">
      <div class="booking-hero__content">
        <div>
          <div class="booking-hero__eyebrow">Medical Workspace</div>
          <h2 class="booking-hero__title mb-2">
            Dr. {{ doctorData?.user?.firstName }} {{ doctorData?.user?.lastName }}
          </h2>
          <div class="booking-hero__meta">
            <span><i class="bi bi-hospital me-1 text-primary"></i>{{ doctorData?.specialization | specializationLabel
              }}</span>
            <span><i class="bi bi-envelope me-1"></i>{{ doctorData?.user?.email }}</span>
            <span>
              <i class="bi me-1" [ngClass]="[clinicStatusDisplay.icon, clinicStatusDisplay.textClass]"></i>
              <span [ngClass]="clinicStatusDisplay.textClass">{{
                clinicStatusDisplay.label
                }}</span>
            </span>
          </div>
        </div>

        <div class="booking-hero__status">
          @if (isClinicActive) {
          <span class="status-pill status-confirmed">
            <i class="bi bi-check-circle-fill me-1"></i>Doctor On Duty
          </span>
          }
          <span class="status-pill" [ngClass]="clinicStatusDisplay.pillClass">
            <i class="bi me-1" [ngClass]="clinicStatusDisplay.icon"></i>{{ clinicStatusDisplay.label }}
          </span>
          <div class="booking-hero__status-note">
            Experience: {{ doctorData?.experienceYears ?? '0' }} years
          </div>
        </div>
      </div>

      @if (pendingInvitations.length > 0) {
      <div
        class="hero-notice-banner p-3 mt-3 rounded-4 d-flex flex-wrap align-items-center justify-content-between gap-3"
        style="
              background: linear-gradient(135deg, #fff9e6 0%, #ffefc2 100%);
              border-left: 5px solid #ffc107;
              box-shadow: 0 4px 12px rgba(217, 119, 6, 0.12);
            ">
        <div class="d-flex align-items-center gap-3">
          <div
            class="bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style="width: 44px; height: 44px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);">
            <i class="bi bi-envelope-exclamation-fill fs-5"></i>
          </div>
          <div>
            <div class="fw-bold text-dark fs-6">
              @if (pendingInvitations.length === 1) {
              Practice Invitation Received from {{ pendingInvitations[0].clinicName }}
              } @else {
              You have {{ pendingInvitations.length }} Pending Practice Invitations!
              }
            </div>
            <div class="text-secondary small">
              @if (pendingInvitations.length === 1) {
              Invited by
              <strong>{{ pendingInvitations[0].invitedByFirstName }}
                {{ pendingInvitations[0].invitedByLastName }}</strong>. Accept the invite to join this practice.
              } @else {
              Please review and accept your practice invitations to enable practice options and
              features.
              }
            </div>
          </div>
        </div>
        <button type="button"
          class="btn btn-warning btn-sm px-3 py-2 fw-bold text-dark rounded-3 shadow-sm d-inline-flex align-items-center gap-2"
          (click)="navigateToProfile()">
          <i class="bi bi-arrow-right-circle-fill"></i> View & Accept Invitation
        </button>
      </div>
      }

      @if (!isClinicActive) {
      <div
        class="hero-notice-banner p-3 mt-3 rounded-4 d-flex flex-wrap align-items-center justify-content-between gap-3"
        style="
              background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
              border-left: 5px solid #3b82f6;
              box-shadow: 0 4px 12px rgba(59, 130, 246, 0.12);
            ">
        <div class="d-flex align-items-center gap-3">
          <div
            class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style="width: 44px; height: 44px; box-shadow: 0 2px 6px rgba(13, 110, 253, 0.2);">
            <i class="bi bi-info-circle-fill fs-5"></i>
          </div>
          <div>
            <div class="fw-bold text-dark fs-6">Practice Status: {{ clinicStatusDisplay.label }}</div>
            <div class="text-secondary small">
              Menu options are unavailable and cannot be selected until the practice status is active.
            </div>
          </div>
        </div>
      </div>
      }
    </div>

    <!-- PAGE CONTENT -->
    <div class="row g-4">
      <!-- LEFT: Personal Tasks -->
      <div class="col-12 col-xl-4 d-flex flex-column gap-3">
        <personal-schedule (taskCreated)="onTaskCreated()"></personal-schedule>

        <!-- Upcoming Todo Tasks -->
        <div class="card booking-card">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">Future Tasks</div>
              <h3 class="sub-title mb-0">
                <i class="bi bi-list-check me-2" style="color: #15803d"></i>Upcoming Todos
              </h3>
            </div>
            @if (upcomingTodos.length > 0) {
            <span class="badge" style="background: #15803d; color: #fff">{{
              upcomingTodos.length
              }}</span>
            }
          </div>

          <div class="card-body p-3">
            @if (todosLoading) {
            <div class="text-center py-3">
              <div class="spinner-border spinner-border-sm" style="color: #15803d"></div>
              <div class="mt-2 text-muted small">Loading upcoming tasks...</div>
            </div>
            }

            @if (!todosLoading && upcomingTodos.length === 0) {
            <div class="empty-state empty-state--compact">
              <i class="bi bi-check2-all fs-2 d-block mb-1" style="color: #15803d"></i>
              <h6>All clear ahead!</h6>
              <p class="text-muted small mb-0">No upcoming tasks scheduled.</p>
            </div>
            }

            <div class="d-flex flex-column gap-2">
              @for (todo of paginatedTodos; track todo.id) {
              <div class="todo-item p-2 rounded-3 border" style="border-left: 3px solid #15803d !important">
                <div class="d-flex align-items-start justify-content-between gap-2">
                  <div class="flex-grow-1 min-w-0">
                    <div class="fw-semibold small">{{ todo.title }}</div>
                    @if (todo.description) {
                    <div class="text-muted" style="font-size: 0.72rem">
                      {{ todo.description }}
                    </div>
                    }
                    <div class="d-flex align-items-center gap-2 mt-1">
                      <span class="badge rounded-pill" [ngClass]="{
                              'bg-danger-subtle text-danger border border-danger-subtle':
                                todo.priority === 'HIGH',
                              'bg-warning-subtle text-warning border border-warning-subtle':
                                todo.priority === 'MEDIUM',
                              'bg-success-subtle text-success border border-success-subtle':
                                todo.priority === 'LOW',
                            }" style="font-size: 0.65rem">{{ todo.priority }}</span>
                      <small class="text-muted" style="font-size: 0.7rem">
                        <i class="bi bi-calendar-event me-1"></i>{{ todo.todoDate | date: 'dd MMM' }}
                      </small>
                    </div>
                  </div>
                  <div class="dropdown">
                    <button class="btn btn-sm btn-light border" type="button" data-bs-toggle="dropdown"
                      aria-expanded="false" title="Task actions">
                      <i class="bi bi-three-dots-vertical"></i>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end shadow-sm">
                      <li>
                        <button class="dropdown-item text-danger" (click)="deleteUpcomingTodo(todo.id)">
                          <i class="bi bi-trash me-2"></i>Delete
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              }
            </div>

            <!-- Pagination Controls -->
            @if (todosPageSize > 1 && !todosLoading && upcomingTodos.length > 0) {
            <div class="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
              <small class="text-muted">
                Page {{ todosCurrentPage }} of {{ todosPageSize }}
              </small>

              <div class="btn-group btn-group-sm" role="group">
                <button type="button" class="btn btn-outline-secondary" [disabled]="todosCurrentPage === 1"
                  (click)="todosPreviousPage()">
                  <i class="bi bi-chevron-left"></i> Previous
                </button>
                <button type="button" class="btn btn-outline-secondary" [disabled]="todosCurrentPage === todosPageSize"
                  (click)="todosNextPage()">
                  Next <i class="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
            }
          </div>
        </div>
      </div>

      <!-- RIGHT: Schedule Calendar -->
      <div class="col-12 col-xl-8 d-flex flex-column gap-3">
        <div class="card booking-card">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">Appointments Overview</div>
              <h3 class="sub-title mb-0">
                <i class="bi bi-calendar3 me-2 text-primary"></i>Practice Schedule
              </h3>
            </div>
          </div>

          <div class="card-body p-0">
            @if (!!doctorId) {
            <schedule-calendar-view #calendar [doctorId]="doctorId" [appearanceSettings]="appearanceSettings">
            </schedule-calendar-view>
            }
          </div>
        </div>
      </div>
    </div>
    <!-- Footer Link -->
    <div class="text-center mt-5 mb-2 d-flex justify-content-center gap-4">
      <a href="javascript:void(0)" class="text-muted small text-decoration-underline" data-bs-toggle="modal"
        data-bs-target="#privacyPolicyModal">
        <i class="bi bi-file-text me-1"></i>Terms &amp; Conditions
      </a>
      <a href="javascript:void(0)" class="text-muted small text-decoration-underline" data-bs-toggle="modal"
        data-bs-target="#contactUsModal">
        <i class="bi bi-envelope me-1"></i>Contact Us
      </a>
    </div>
  </div>
</div>

<!-- Privacy Policy / Terms & Conditions Modal -->
<app-terms-and-conditions-modal></app-terms-and-conditions-modal>

<!-- Contact Us Modal -->
<app-contact-us-modal></app-contact-us-modal>
}